import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Language } from '../models/language.model';
import { LocalizedName } from '../models/localized-name.model';
import { ConfigService } from './config.service';
import { AdminQuestion, Answer } from '../models/questions';

@Injectable()
export class SaveFormService {
  saveButtonClicked$: Observable<{
    question: AdminQuestion;
    isAdd: boolean;
  }> = new Observable();
  addInputButtonClicked$: Observable<{ isColumn: boolean }> = new Observable();
  formClosed$: Observable<AdminQuestion> = new Observable();
  formValueChanged$: Observable<boolean> = new Observable();

  private saveButtonSubject = new Subject<{
    question: AdminQuestion;
    isAdd: boolean;
  }>();
  private formClosedSubject = new Subject<AdminQuestion>();
  private formValueChangedSubject = new Subject<boolean>();
  private addInputButtonSubject = new Subject<{ isColumn: boolean }>();
  private languages: Array<Language>;

  constructor(private appData: ConfigService) {
    this.saveButtonClicked$ = this.saveButtonSubject.asObservable();
    this.addInputButtonClicked$ = this.addInputButtonSubject.asObservable();
    this.formClosed$ = this.formClosedSubject.asObservable();
    this.formValueChanged$ = this.formValueChangedSubject.asObservable();
    this.getAllLanguages();
  }

  addButtonClicked(isColumn = false) {
    this.addInputButtonSubject.next({ isColumn });
  }

  saveButtonClicked(question: AdminQuestion, isAdd = false) {
    this.saveButtonSubject.next({ question: question, isAdd: isAdd });
  }

  formValueChanged(isChanged: boolean) {
    this.formValueChangedSubject.next(isChanged);
  }

  closeForm(question: AdminQuestion) {
    this.formClosedSubject.next(question);
  }

  mapFormGroupControlToPropArray(
    form: FormGroup,
    partOfControlName: string
  ): Array<string> {
    const propertiesArray: Array<string> = [];
    const formQuestion = form.value;
    for (const key in formQuestion) {
      if (
        formQuestion.hasOwnProperty(key) &&
        key.indexOf(partOfControlName) >= 0
      ) {
        propertiesArray.push(key);
      }
    }
    return propertiesArray;
  }

  addInputToForm(
    questionForm: FormGroup,
    partOfControlName: string,
    numberOfFieldGroup
  ) {
    this.languages.forEach((l) => {
      questionForm.addControl(
        `${partOfControlName}_${numberOfFieldGroup}_${l.code}`,
        new FormControl('')
      );
    });
  }

  addAllLocalizedNameToFormGroup(
    questionForm: FormGroup,
    partOfControlName: string,
    localizedNames: Array<LocalizedName> = []
  ) {
    if (localizedNames.length === 0) {
      this.mapEmptyLocalizedNamesToFormGroup(
        this.languages,
        questionForm,
        partOfControlName
      );
    } else {
      this.mapLocalizedNamesToFormGroup(
        localizedNames,
        questionForm,
        partOfControlName
      );
    }
  }

  addRatingValueToFormGroup(
    questionForm: FormGroup,
    partOfControlName: string,
    answer: Answer = {}
  ) {
    if (!answer.id) {
      questionForm.addControl(`${partOfControlName}`, new FormControl(''));
    } else {
      questionForm.addControl(
        `${partOfControlName}`,
        new FormControl(answer.order.toString())
      );
    }
  }

  mapFormvalueToQuestion(formValue: object, partOfControlName) {
    const localizedNames: Array<LocalizedName> = [];
    for (const key in formValue) {
      if (
        formValue.hasOwnProperty(key) &&
        key.indexOf(partOfControlName) >= 0
      ) {
        this.languages.forEach((lang) => {
          if (key.indexOf(lang.code) >= 0) {
            localizedNames.push({ locale: lang.code, name: formValue[key] });
          }
        });
      }
    }
    return localizedNames;
  }

  mapFormvalueRateToQuestion(formValue: object, partOfControlName) {
    const orders: Array<number> = [];
    for (const key in formValue) {
      if (
        formValue.hasOwnProperty(key) &&
        key.indexOf(partOfControlName) >= 0
      ) {
        orders.push(formValue[key]);
      }
    }
    return orders;
  }

  joinLocalizedNames(
    localizedNames: Array<LocalizedName>
  ): Array<Array<LocalizedName>> {
    const localizedNamesArray: Array<Array<LocalizedName>> = [];
    const temp: Array<{ index: number; lNames: Array<LocalizedName> }> = [];
    this.languages.forEach((l, i) => {
      temp.push({
        index: i,
        lNames: localizedNames.filter((x) => x.locale === l.code),
      });
    });
    temp.forEach((x) => {
      x.lNames.forEach((l, i) => {
        if (!localizedNamesArray[i]) {
          localizedNamesArray.splice(i, 0, [l]);
        } else {
          localizedNamesArray[i].push(l);
        }
      });
    });
    return localizedNamesArray;
  }

  setPlaceholderArray(
    formControls: any,
    placeholder = 'Enter name in'
  ): Array<string> {
    const placeholderArray: Array<string> = [];
    for (const key in formControls) {
      if (formControls.hasOwnProperty(key)) {
        const languageCode = [...key.split('_')].pop();
        placeholderArray.push(`${placeholder} ${languageCode.toLowerCase()}`);
      }
    }
    return placeholderArray;
  }

  getAllFormControlsFields(formControls: any): Array<string> {
    const fieldsProperties: Array<string> = [];
    for (const key in formControls) {
      if (formControls.hasOwnProperty(key)) {
        fieldsProperties.push(key);
      }
    }
    return fieldsProperties;
  }

  setPlaceholderArrayByPropName(
    formControlsField: Array<string>,
    placeholder = 'Enter name in'
  ): Array<string> {
    const placeholderArray: Array<string> = [];
    formControlsField.forEach((x) => {
      const languageCode = [...x.split('_')].pop();
      placeholderArray.push(`${placeholder} ${languageCode.toLowerCase()}`);
    });
    return placeholderArray;
  }

  getControlsByControlName(
    controlName: string,
    allControlsName: Array<string>
  ): Array<string> {
    return allControlsName.filter((x) => x.includes(controlName));
  }

  addValidationToLocalizedControls(
    questionForm: FormGroup,
    partOfControlName: string
  ) {
    const allControls = this.getAllFormControlsFields(questionForm.controls);
    const titleControls = this.getControlsByControlName(
      partOfControlName,
      allControls
    );
    titleControls.forEach((x) => {
      questionForm.controls[x].setValidators([
        Validators.required,
        Validators.minLength(this.appData.configData.answerTextMinLength),
      ]);
    });
  }

  addValidationToNumberTypeControls(
    questionForm: FormGroup,
    partOfControlName: string
  ) {
    const allControls = this.getAllFormControlsFields(questionForm.controls);
    const titleControls = this.getControlsByControlName(
      partOfControlName,
      allControls
    );
    titleControls.forEach((x) =>
      questionForm.controls[x].setValidators([Validators.required])
    );
  }

  canShowFormControlFieldError(
    questionForm: FormGroup,
    formControlName: string
  ) {
    return questionForm.get(formControlName).invalid;
  }

  private getAllLanguages() {
    this.languages = this.appData.getLanguages;
  }

  private mapEmptyLocalizedNamesToFormGroup(
    languages: Array<Language>,
    questionForm: FormGroup,
    partOfControlName: string
  ) {
    languages.forEach((l) => {
      questionForm.addControl(
        `${partOfControlName}${l.code}`,
        new FormControl('')
      );
    });
  }

  private mapLocalizedNamesToFormGroup(
    localizedNames: Array<LocalizedName>,
    questionForm: FormGroup,
    partOfControlName: string
  ) {
    localizedNames.forEach((l) => {
      questionForm.addControl(
        `${partOfControlName}${l.locale}`,
        new FormControl(l.name)
      );
    });
  }
}
